<template>
  <aside :class="[
    'flex  border-slate-200 z-30 bg-gray-50',
    // Responsive positioning - bottom on mobile, left side on larger screens
    isMobile ? 'fixed bottom-0 left-0 right-0 h-12 border-t w-full flex-row justify-around items-center' : 'fixed flex-col top-0 h-full border-r ',
    !isMobile && (store.isSidebarOpen ? 'w-72' : 'w-16')
  ]">
    <!-- Only show on desktop -->
    <div v-if="!isMobile" class="flex flex-1 flex-col overflow-hidden">
      <!-- Sidebar Toggle Button -->
      <div :class="['p-4', !store.isSidebarOpen ? 'flex justify-center' : 'pl-5']">
        <button class="top-button sm:hover:bg-gray-200 rounded-lg" @click="toggleSidebar"
          v-tippy="{ content: 'Toggle Sidebar', delay: [500, 0] }">
          <img src="/assets/sidebar.svg" alt="Menu" class="w-6 h-6" />
        </button>
      </div>

      <!-- Logo -->
      <div class="pl-4 pb-2" v-if="store.isSidebarOpen">
        <span class="text-xl lg:text-3xl font-thin whitespace-nowrap">Nevua Markets</span>
        <p class="text-xs md:text-sm font-thin hidden lg:block whitespace-nowrap">Create Polymarket watchlists.</p>
        <p class="text-xs md:text-sm font-thin hidden lg:block whitespace-nowrap">Get real-time alerts for events that matter.</p>
      </div>

      <!-- Search -->
      <div :class="[
        'whitespace-nowrap rounded-lg items-center text-lg',
        store.isSidebarOpen ? 'p-3 justify-left' : 'p-4 flex justify-center',
        selectedPath === '/' ? 'bg-gray-200' : 'hover:bg-gray-100'
      ]">
        <RouterLink to="/" class="flex items-center" @click="selectPath('/')">
          <img src="/assets/search.svg" alt="Search" class="w-5 h-5" />
          <span v-if="store.isSidebarOpen" class="ml-2">New Watchlist</span>
        </RouterLink>
      </div>

      <!-- Watchlists Section -->
      <div class="whitespace-nowrap overflow-y-auto">
        <h2 class="text-lg sticky top-0 bg-gray-50 z-10" :class="[
          store.isSidebarOpen ? 'p-3' : 'p-4 flex justify-center'
        ]">
          <span class="flex items-center cursor-pointer" :class="[
            store.isSidebarOpen ? 'justify-left' : 'justify-center'
          ]" @click="toggleWatchlist">
            <i class="fas fa-list" :class="[store.isSidebarOpen ? 'mr-2' : '']"></i>
            <span v-if="store.isSidebarOpen">
              Watchlists
              <i
                :class="['ml-2 fas transition-transform duration-300', isWatchlistExpanded ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
            </span>
          </span>
        </h2>
        <ul v-if="store.isSidebarOpen" class="w-[95%] mx-auto transition-all duration-300 ease-in-out origin-top"
          :class="[isWatchlistExpanded ? 'opacity-100' : 'max-h-0 opacity-0']">
          <li v-for="item in store.watchLists" :key="item.name" :class="[
            'relative group/item rounded-lg',
            selectedPath === getWatchListPath(item) ? 'bg-gray-200' : 'hover:bg-gray-100'
          ]">
            <div v-if="editingWatchList.hasOwnProperty(item.id) && editingWatchList[item.id]" class="relative pl-8 py-2">
              <EditableInput :value="item.name" class="w-full bg-transparent" @keydown.enter="saveKeydown(item, $event)"
                @keydown.escape="discardEdit(item)" @blur="saveBlur(item, $event)" />
            </div>
            <RouterLink v-else :to="getWatchListPath(item)" :class="[
              'flex items-center justify-between min-w-0 pl-8 py-2',
              store.loadingWatchlistId ? 'opacity-50 pointer-events-none' : ''
            ]" @click="selectItemPath(item)" v-tippy="{ content: item.name, delay: [500, 0], touch: false }">
              <span class="truncate flex-grow">{{ item.name }}</span>
              <div v-show="store.loadingWatchlistId === item.id"
                class="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-900"></div>
              <!-- Desktop controls -->
              <div v-show="!store.loadingWatchlistId"
                class="hidden md:flex space-x-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 shrink-0">
                <i v-if="false" class="far fa-edit cursor-pointer" @click="startEditing(item, $event)"></i>
                <i v-if="false" class="far fa-trash-alt cursor-pointer"
                  @click="deleteWatchListFromSidebar(item, $event)"></i>
              </div>
              <!-- Mobile controls -->
              <div class="md:hidden">
                <i class="fas fa-ellipsis cursor-pointer" @click.stop="showContextMenu(item, $event)"></i>
              </div>
            </RouterLink>
          </li>
        </ul>
      </div>

      <!-- Sticky Footer for desktop -->
      <div class="mt-auto sticky bottom-0">
        <!-- Dialog Test -->
        <!-- <div :class="[
          'whitespace-nowrap rounded-lg flex items-center',
          store.isSidebarOpen ? 'p-3 hover:bg-gray-100' : 'p-4 justify-center hover:bg-gray-100'
        ]">
          <i class="fas fa-bell" :class="[store.isSidebarOpen ? 'mr-2' : '']"></i>
          <button v-if="store.isSidebarOpen" @click="showDialog">
            Dialog Test
          </button>
        </div> -->

        <div :class="[
          'flex-shrink-0 border-t border-slate-200 cursor-pointer whitespace-nowrap',
          store.isSidebarOpen ? 'p-3' : 'p-4 flex justify-center'
        ]" @click="openChat">
          <i class="fas fa-comment" :class="[store.isSidebarOpen ? 'mr-2' : '']"></i>
          <span v-if="store.isSidebarOpen">Talk to Us!</span>
        </div>
        
        <div v-if="store.isAuthenticated" class="flex-shrink-0 border-t border-slate-200">
          <Menu as="div" class="relative">
            <MenuButton :class="[
              'text-left text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center',
              store.isSidebarOpen ? 'w-full p-4 justify-between' : 'p-4 justify-center'
            ]">
              <span v-if="store.isSidebarOpen">{{ store.email }}</span>
              <div v-else class="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center">
                {{ store.email ? store.email.charAt(0).toUpperCase() : 'U' }}
              </div>
              <img v-if="store.isSidebarOpen" src="/assets/resize-small.svg" alt="Expand" class="w-7 h-7" />
            </MenuButton>

            <MenuItems
              class="absolute bottom-full left-0 mb-2 w-full rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div class="py-1">
                <MenuItem v-slot="{ active }" v-if="!store.paid">
                <button class="w-full text-left px-4 py-2"
                  :class="[
                      'bg-white hover:bg-gray-300 text-black w-full h-10 font-light text-base font-sans rounded-lg whitespace-nowrap flex items-center justify-center gap-2',
                    ]"
                  @click="handleEnterLicenseKey()">
                  Enter License Key
                </button>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                <button class="w-full text-left px-4 py-2"
                  :class="[
                      'bg-white hover:bg-gray-300 text-black w-full h-10 font-light text-base font-sans rounded-lg whitespace-nowrap flex items-center justify-center gap-2',
                    ]"
                  @click="handleLogout">
                  Logout
                </button>
                </MenuItem>
              </div>
            </MenuItems>
          </Menu>
        </div>
        <div v-else :class="[
          'flex-shrink-0 border-t border-slate-200',
          store.isSidebarOpen ? 'p-4' : 'p-4 flex justify-center'
        ]">
          <div v-if="store.isSidebarOpen">
            <LoginMenu />
          </div>
          <div v-else class="w-8 h-8 rounded-full bg-gray-300 text-white flex items-center justify-center">
            <i class="fas fa-user"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile bottom navigation bar -->
    <div v-if="isMobile" class="flex flex-row justify-around items-end w-full">
      <!-- Search button -->
      <div class="p-1.5 pb-2">
        <RouterLink to="/" class="flex flex-col items-center" @click="selectPath('/')">
          <img src="/assets/search.svg" alt="Search" class="w-5 h-5" />
          <span class="text-[10px] mt-0.5">Search</span>
        </RouterLink>
      </div>
      
      <!-- Watchlist button -->
      <div class="p-1.5 pb-2">
        <div @click="toggleWatchlist" class="flex flex-col items-center cursor-pointer">
          <i class="fas fa-list text-lg"></i>
          <span class="text-[10px] mt-0.5">Lists</span>
        </div>
      </div>
      
      <!-- Notifications button -->
      <div class="p-1.5 pb-2" v-if="store.isAuthenticated">
        <div @click="toggleNotifications" class="flex flex-col items-center cursor-pointer">
          <div class="relative">
            <i class="fas fa-bell text-lg"></i>
            <span v-if="unreadNotificationsCount() > 0"
              class="absolute -top-1 -right-1 flex items-center justify-center min-w-[16px] h-4 px-1 bg-red-500 text-white text-[8px] rounded-full">
              {{ unreadNotificationsCount() }}
            </span>
          </div>
          <span class="text-[10px] mt-0.5">Alerts</span>
        </div>
      </div>
      
      <!-- Chat button -->
      <div class="p-1.5 pb-2">
        <div @click="openChat" class="flex flex-col items-center cursor-pointer">
          <i class="fas fa-comment text-lg"></i>
          <span class="text-[10px] mt-0.5">Chat</span>
        </div>
      </div>
      
      <!-- User profile/login button -->
      <div class="p-1.5 pb-2">
        <div class="flex flex-col items-center cursor-pointer" @click="toggleUserMenu">
          <div v-if="store.isAuthenticated" class="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px]">
            {{ store.email ? store.email.charAt(0).toUpperCase() : 'U' }}
          </div>
          <i v-else class="fas fa-user text-lg"></i>
          <span class="text-[10px] mt-0.5">{{ store.isAuthenticated ? 'Account' : 'Login' }}</span>
        </div>
      </div>
    </div>

    <!-- Mobile user menu popup -->
    <div v-if="showMobileUserMenu && isMobile" class="fixed bottom-20 right-4 bg-white shadow-lg rounded-lg py-2 w-48 z-40">
      <div v-if="store.isAuthenticated" class="px-4 py-2 border-b">
        <div class="text-sm font-medium">{{ store.email }}</div>
      </div>
      <div v-if="store.isAuthenticated" class="px-4 py-2 hover:bg-gray-100 cursor-pointer" @click="handleLogout">
        Logout
      </div>
      <div v-else class="px-4 py-2">
        <LoginMenu />
      </div>
    </div>

    <!-- Add at the bottom of template, before closing nav tag -->
    <div v-if="contextMenu.show" class="fixed bg-white shadow-lg rounded-lg py-2"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }">
      <div class="px-4 py-2 hover:bg-gray-100 cursor-pointer" @click="handleContextMenuAction('rename')">
        Rename
      </div>
      <div class="px-4 py-2 hover:bg-gray-100 cursor-pointer" @click="handleContextMenuAction('delete')">
        Delete
      </div>
    </div>

    <!-- Add this before closing template tag -->
    <TransitionRoot appear :show="isDeleteModalOpen" as="template">
      <Dialog as="div" @close="closeDeleteModal" class="relative z-50">
        <div class="fixed inset-0 bg-black/30" />
        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6">
              <DialogTitle class="text-lg font-medium mb-4">Confirm Deletion</DialogTitle>
              <p class="mb-6">Are you sure you want to delete watchlist '{{ itemToDelete?.name }}'?</p>
              <div class="flex justify-end space-x-3">
                <button @click="closeDeleteModal" class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">
                  Cancel
                </button>
                <button @click="confirmDelete" class="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white">
                  Delete
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    
    <!-- Mobile watchlist popup drawer -->
    <div v-if="isMobile && isWatchlistExpanded" class="fixed bottom-16 left-0 right-0 max-h-[50vh] overflow-y-auto bg-white shadow-lg border-t border-slate-200 z-40 p-2">
      <div class="flex justify-between items-center mb-2">
        <h2 class="font-medium">Your Watchlists</h2>
        <button @click="toggleWatchlist" class="p-1 hover:bg-gray-100 rounded-full">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <ul>
        <li v-for="item in store.watchLists" :key="item.name" :class="[
          'rounded-lg p-3',
          selectedPath === getWatchListPath(item) ? 'bg-gray-200' : 'hover:bg-gray-100'
        ]">
          <RouterLink :to="getWatchListPath(item)" class="flex items-center justify-between" @click="selectItemPath(item)">
            <span class="truncate">{{ item.name }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </RouterLink>
        </li>
        <li v-if="store.watchLists.length === 0" class="p-3 text-gray-500">
          No watchlists found
        </li>
      </ul>
    </div>

    <!-- Add before closing template tag -->
    <AuthModal 
      :isOpen="showAuthModal" 
      @close="showAuthModal = false"
    />
  </aside>
</template>

<script>
import EditableInput from './EditableInput.vue';
import { 
  store, 
  resetStore, 
  deleteWatchList, 
  getWatchListPath, 
  updateWatchList, 
  showDialog, 
  toggleNotifications, 
  unreadNotificationsCount, 
} from '../store';
import crispService from '../services/crisp';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import LoginMenu from './menus/LoginMenu.vue';
import AuthModal from './AuthModal.vue';

export default {
  name: 'Sidebar',
  props: {
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const breakpoints = useBreakpoints(breakpointsTailwind)

    return {
      store,
      selectedPath: "",
      disableBlur: false,
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        selectedItem: null
      },
      editingWatchList: {},
      isEmailLoginOpen: false,
      emailInput: '',
      isDeleteModalOpen: false,
      itemToDelete: {},
      isWatchlistExpanded: true,
      breakpoints,
      showMobileUserMenu: false,
      showAuthModal: false
    }
  },
  created() {
    if (this.isMobile) {
      this.isWatchlistExpanded = false;
    }
  },
  components: {
    EditableInput,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    LoginMenu,
    AuthModal
  },
  watch: {
    '$route.params': {
      handler(newParams, oldParams) {
        if (newParams.listId && newParams.listId !== oldParams?.listId) {
          const watchList = store?.watchLists?.find(item => item.id === newParams.listId);
          if (watchList) {
            this.selectItemPath(watchList);
          }
        } else if (!newParams.listId) {
          this.selectedPath = this.$route.path;
        }
      },
      immediate: true
    }
  },
  methods: {
    showDialog() {
      showDialog({ message: 'This is a dialog with a very very very very very big text very very very big text very very very big text very very very big text very very very big text', timeout: 10000 });
    },
    toggleUserMenu() {
      if (!this.store.isAuthenticated) {
        this.showAuthModal = true;
        return;
      }
      this.showMobileUserMenu = !this.showMobileUserMenu;
    },
    showNotification() {
      return;
    },
    openChat() {
      crispService.client.chat.show();
      crispService.client.chat.open();
    },
    getWatchListPath,
    selectItemPath(item) {
      this.selectedPath = getWatchListPath(item);
      store.tableState.tableName = item.name;
      this.$router.push(this.selectedPath);
      
      // Close watchlist drawer on mobile after selection
      if (this.isMobile) {
        this.isWatchlistExpanded = false;
      }
    },
    selectPath(path) {
      this.selectedPath = path;
      this.$router.push(this.selectedPath);
      
      // Close any open mobile menus
      if (this.isMobile) {
        this.isWatchlistExpanded = false;
        this.showMobileUserMenu = false;
      }
    },
    newSearch() {
      this.selectedPath = '/';
      this.$router.push('/');
      
      // Close any open mobile menus
      if (this.isMobile) {
        this.isWatchlistExpanded = false;
        this.showMobileUserMenu = false;
      }
    },
    async deleteWatchlistFromSidebar(item, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.itemToDelete = item;
      this.isDeleteModalOpen = true;
    },
    closeDeleteModal() {
      this.isDeleteModalOpen = false;
      this.itemToDelete = {};
    },
    async confirmDelete() {
      if (this.itemToDelete) {
        await deleteWatchList(this.itemToDelete?.id);
        if (this.$route.path.includes(this.itemToDelete?.id)) {
          this.$router.push('/');
          this.selectedPath = '/';
        }
      }
      this.closeDeleteModal();
    },
    async startEditing(item, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.editingWatchList[item.id] = true;
    },
    async saveKeydown(item, event) {
      // the blur dance likely is becauase two events can fire with keydown and then with blur
      this.disableBlur = true;
      await this.saveEdit(item, event);
      this.$nextTick(() => {
        this.disableBlur = false;
      });
    },
    async saveBlur(item, event) {
      if (this.disableBlur) {
        this.disableBlur = false;
        return;
      }
      await this.saveEdit(item, event);
    },
    async saveEdit(item, event) {
      const newName = event.target.value.trim();
      if (newName !== '') {
        const watchList = store?.watchLists?.find(i => i.id === item.id);
        if (watchList) {
          watchList.name = newName;
          if (this.selectedPath === getWatchListPath(item)) {
            store.tableState = {
              ...store.tableState,
              name: newName
            };
          }

          await updateWatchList(watchList.id, {
            name: newName
          });
        }
      }
      this.editingWatchList[item.id] = false;
    },
    editTitle(item) {
      this.editingWatchList[item.id] = true;
    },
    discardEdit(item) {
      this.editingWatchList[item.id] = false;
    },
    toggleSidebar() {
      store.isSidebarOpen = !store.isSidebarOpen;
    },
    showContextMenu(item, event) {
      event.preventDefault();
      this.contextMenu.show = true;
      this.contextMenu.x = event.clientX;
      this.contextMenu.y = event.clientY;
      this.contextMenu.selectedItem = item;
    },
    handleContextMenuAction(action) {
      if (action === 'rename') {
        this.startEditing(this.contextMenu.selectedItem);
      } else if (action === 'delete') {
        this.deleteWatchlistFromSidebar(this.contextMenu.selectedItem);
      }
      this.contextMenu.show = false;
    },
    handleSettings() {
      console.log('Settings clicked')
    },
    async handleEnterLicenseKey() {
      store.verifyLicenseKeyModal = true;
    },
    async handleLogout() {
      localStorage.removeItem('jwtToken');
      this.store.isAuthenticated = false;
      resetStore();
      crispService.client.session.reset()
      if (this.isMobile) {
        crispService.client.chat.hide()
      }
      
      this.showMobileUserMenu = false;
      this.$router.push({ path: '/' });
      store.isLoggedOut = true;
    },
    async handleEmailLogin() {
      console.log('Email login with:', this.emailInput);
      this.isEmailLoginOpen = false;
      this.emailInput = '';
    },
    toggleWatchlist() {
      this.isWatchlistExpanded = !this.isWatchlistExpanded;
      // Close user menu if open
      if (this.showMobileUserMenu) {
        this.showMobileUserMenu = false;
      }
    },
    toggleNotifications() {
      toggleNotifications();
      // Close any open mobile menus
      if (this.isMobile) {
        this.isWatchlistExpanded = false;
        this.showMobileUserMenu = false;
      }
    },
    unreadNotificationsCount,
  },
  mounted() {
    this.selectedPath = this.$route.path;
    window.addEventListener('click', (event) => {
      // Close context menu on click outside
      this.contextMenu.show = false;
      
      // Close mobile user menu on click outside
      if (this.showMobileUserMenu) {
        const target = event.target;
        if (!this.$el.contains(target) || (target.closest('[data-close-menu]'))) {
          this.showMobileUserMenu = false;
        }
      }
    });
  },
};
</script>

<style scoped>
.mobile-menu-item {
  @apply flex flex-col items-center justify-center p-1;
}
</style>